import React, { useState, useEffect } from "react";
import Icon from "../../../components/Icon";
import { StatusBadge } from "../../../components/Badge";
import Skeleton from "../../../components/Skeleton";
import { EmptyState, ErrorState } from "../../../components/State";
import ServerDetailModal from "../../../components/ServerDetailModal";
import Tooltip from "../../../components/Tooltip";
import { getAssetCategory, formatCategoryLabel, formatManagementLabel } from "../../../utils/assetTaxonomy";
import { useAssetStore } from "../../../store/useAssetStore";

export default function NetworkView() {
  const routers = useAssetStore((state) => state.routers);
  const summary = useAssetStore((state) => state.summaryStats);
  const loading = useAssetStore((state) => state.loading);
  const error = useAssetStore((state) => state.error);
  const fetchAllData = useAssetStore((state) => state.fetchAllData);

  const [selectedRouter, setSelectedRouter] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  useEffect(() => {
    fetchAllData();
    const interval = setInterval(() => {
      fetchAllData(true);
    }, 10000);
    return () => clearInterval(interval);
  }, [fetchAllData]);

  useEffect(() => {
    if (selectedRouter) {
      const updated = routers.find(
        (r) => r.id === selectedRouter.id || r.hostname === selectedRouter.hostname
      );
      if (updated) setSelectedRouter(updated);
    }
  }, [routers]);

  const filteredRouters = routers.filter((r) => {
    const matchesSearch =
      r.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      r.hostname?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      r.vendor?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      r.model?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = !statusFilter || r.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const statCards = [
    {
      label: "Total Appliances",
      value: routers.length,
      color: "#6B21A8",
      bg: "#F3E8FF",
      icon: "network",
      filterValue: "",
    },
    {
      label: "Active / Healthy",
      value: routers.filter(
        (r) =>
          r.status === "active" &&
          r.specs?.is_responsive !== false &&
          r.specs?.snmp_status !== "unreachable"
      ).length,
      color: "#15803D",
      bg: "#F0FDF4",
      icon: "check",
      filterValue: "active",
    },
    {
      label: "Degraded",
      value: routers.filter(
        (r) =>
          r.status === "degraded" ||
          (r.specs?.health_score &&
            r.specs.health_score < 80 &&
            r.specs.health_score > 0)
      ).length,
      color: "#D97706",
      bg: "#FEF3C7",
      icon: "alert",
      filterValue: "degraded",
    },
    {
      label: "Inactive / Down",
      value: routers.filter(
        (r) =>
          r.status === "inactive" ||
          r.status === "decommissioned" ||
          r.specs?.is_responsive === false ||
          r.specs?.snmp_status === "unreachable" ||
          r.specs?.health_score === 0
      ).length,
      color: "#DC2626",
      bg: "#FEF2F2",
      icon: "alert",
      filterValue: "inactive",
    },
  ];

  return (
    <div
      style={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        background: "#F8FAFC",
      }}
    >
      {/* Top Header Container matching CMDBView & DiscoveryView */}
      <div
        style={{
          padding: "18px 24px 14px",
          borderBottom: "1px solid #E5E7EB",
          background: "#fff",
        }}
      >
        <div style={{ marginBottom: 14 }}>
          <h1
            style={{
              margin: 0,
              fontSize: 18,
              fontWeight: 700,
              color: "#111827",
            }}
          >
            Network Infrastructure
          </h1>
          <p style={{ margin: "2px 0 0", fontSize: 12, color: "#6B7280" }}>
            Live SNMP & ICMP telemetry monitoring for agentless routers, switches, firewalls, and WAN uplinks
          </p>
        </div>

        {/* Operational Summary Cards - Interactive Filters */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(5, 1fr)",
            gap: 10,
            marginBottom: 14,
          }}
        >
          {statCards.map((card) => {
            const isSelected = statusFilter === card.filterValue;
            return (
              <div
                key={card.label}
                onClick={() => setStatusFilter(card.filterValue)}
                style={{
                  background: card.bg,
                  borderRadius: 8,
                  padding: "10px 12px",
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  cursor: "pointer",
                  border: isSelected ? `2px solid ${card.color}` : `1px solid ${card.bg}`,
                  boxShadow: isSelected ? `0 2px 8px ${card.bg}` : "none",
                  transition: "all 0.15s ease",
                }}
              >
                <div
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: 8,
                    background: "#fff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}
                >
                  <Icon name={card.icon} size={16} color={card.color} />
                </div>
                <div>
                  <div
                    style={{
                      fontSize: 11,
                      fontWeight: 600,
                      color: "#64748B",
                      textTransform: "uppercase",
                      letterSpacing: "0.04em",
                    }}
                  >
                    {card.label}
                  </div>
                  <div
                    style={{
                      fontSize: 20,
                      fontWeight: 800,
                      color: "#0F172A",
                      marginTop: 2,
                    }}
                  >
                    {loading ? <Skeleton width={40} height={20} /> : card.value}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Filter and Search Bar */}
        <div style={{ display: "flex", gap: 10 }}>
          <div style={{ flex: 1, position: "relative", display: "flex", alignItems: "center" }}>
            <Icon name="search" size={14} color="#9CA3AF" style={{ position: "absolute", left: 10, pointerEvents: "none" }} />
            <input
              placeholder="Search network appliances by name, hostname, vendor, or model..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                width: "100%",
                padding: "7px 12px 7px 32px",
                border: "1px solid #E5E7EB",
                borderRadius: 7,
                fontSize: 12,
                background: "#fff",
                outline: "none",
              }}
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            style={{
              padding: "7px 10px",
              border: "1px solid #E5E7EB",
              borderRadius: 7,
              fontSize: 12,
              background: "#fff",
              color: "#374151",
              cursor: "pointer",
            }}
          >
            <option value="">All Active / Monitored Statuses</option>
            <option value="active">Active / Healthy</option>
            <option value="degraded">Degraded</option>
            <option value="inactive">Inactive / Down</option>
            <option value="decommissioned">Decommissioned</option>
          </select>
        </div>
      </div>

      {/* Live Inventory Table */}
      <div style={{ flex: 1, overflowY: "auto", padding: "16px 24px" }}>
        {loading ? (
          <div style={{ padding: 20, display: "flex", flexDirection: "column", gap: 10 }}>
            {[...Array(5)].map((_, i) => (
              <Skeleton key={i} height={48} style={{ borderRadius: 8 }} />
            ))}
          </div>
        ) : error ? (
          <ErrorState message={error} onRetry={fetchAllData} />
        ) : routers.length === 0 ? (
          <div style={{ padding: 40, textAlign: "center" }}>
            <EmptyState
              icon="network"
              title="No Network Routers Monitored"
              subtitle="No agentless SNMP routers or network appliances have been discovered yet. Once an administrator configures a monitored subnet and runs a discovery sweep, core network devices will automatically appear here."
            />
          </div>
        ) : filteredRouters.length === 0 ? (
          <EmptyState icon="search" title="No matching network assets" subtitle="No network appliances match your search query or status filter. Try clearing your search query." />
        ) : (
          <table className="premium-table" style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th>Device Name</th>
                <th>Hostname / IP</th>
                <th>Status</th>
                <th>Type</th>
                <th>Vendor & Model</th>
                <th>Latency (Ping)</th>
                <th>Health Score</th>
                <th>Management</th>
              </tr>
            </thead>
            <tbody>
              {filteredRouters.map((router) => (
                <tr
                  key={router.id}
                  onClick={() => setSelectedRouter(router)}
                  style={{ borderBottom: "1px solid #F1F5F9", cursor: "pointer", transition: "background 0.15s ease" }}
                >
                  <td style={{ padding: "12px 14px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <div
                        style={{
                          width: 30,
                          height: 30,
                          borderRadius: 7,
                          background: "#F3E8FF",
                          border: "1px solid #E9D5FF",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <Icon name="network" size={15} color="#6B21A8" />
                      </div>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 700, color: "#0F172A" }}>{router.name}</div>
                        <div style={{ fontSize: 10, color: "#64748B", fontFamily: "monospace" }}>{router.id.slice(0, 8)}…</div>
                      </div>
                    </div>
                  </td>
                  <td style={{ padding: "12px 14px", fontFamily: "monospace", fontSize: 12, color: "#334155" }}>
                    {router.hostname}
                  </td>
                  <td style={{ padding: "12px 14px" }}>
                    <StatusBadge
                      status={
                        router.status === "decommissioned" ||
                        router.specs?.is_responsive === false ||
                        router.specs?.snmp_status === "unreachable" ||
                        router.specs?.health_score === 0
                          ? "inactive"
                          : router.status
                      }
                    />
                  </td>
                  <td style={{ padding: "12px 14px" }}>
                    {(() => {
                      const cat = getAssetCategory(router);
                      const isSwitch = cat === "switch";
                      const isFirewall = cat === "firewall";
                      const isLoadBalancer = cat === "load_balancer";
                      const iconName = isSwitch ? "layers" : isFirewall ? "shield" : isLoadBalancer ? "cpu" : "network";
                      const badgeBg = isSwitch ? "#E0F2FE" : isFirewall ? "#FEE2E2" : isLoadBalancer ? "#FEF3C7" : "#F3E8FF";
                      const badgeColor = isSwitch ? "#0369A1" : isFirewall ? "#991B1B" : isLoadBalancer ? "#92400E" : "#6B21A8";
                      const badgeBorder = isSwitch ? "1px solid #BAE6FD" : isFirewall ? "1px solid #FECACA" : isLoadBalancer ? "1px solid #FDE68A" : "1px solid #E9D5FF";
                      return (
                        <span
                          style={{
                            fontSize: 11,
                            fontWeight: 700,
                            letterSpacing: "0.03em",
                            padding: "3px 8px",
                            borderRadius: 6,
                            background: badgeBg,
                            color: badgeColor,
                            border: badgeBorder,
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 4,
                            textTransform: "uppercase",
                          }}
                        >
                          <Icon name={iconName} size={10} color={badgeColor} />
                          {formatCategoryLabel(cat)}
                        </span>
                      );
                    })()}
                  </td>
                  <td style={{ padding: "12px 14px", maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {(() => {
                      const vendorStr = router.vendor || router.specs?.vendor || "Linux";
                      const modelStr = router.model || router.specs?.model || "Device";
                      const fullStr = modelStr.toLowerCase().startsWith(vendorStr.toLowerCase()) ? modelStr : `${vendorStr} ${modelStr}`;
                      return (
                        <Tooltip text={fullStr}>
                          <span style={{ background: "#F1F5F9", padding: "3px 8px", borderRadius: 6, border: "1px solid #E2E8F0", fontWeight: 600, fontSize: 11, color: "#334155", display: "inline-block", maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {fullStr}
                          </span>
                        </Tooltip>
                      );
                    })()}
                  </td>
                  <td style={{ padding: "12px 14px", fontSize: 12, fontWeight: 700, color: router.ping_latency_ms > 50 ? "#D97706" : "#16A34A" }}>
                    {router.ping_latency_ms !== null ? `${router.ping_latency_ms} ms` : "—"}
                  </td>
                  <td style={{ padding: "12px 14px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <div style={{ width: 60, height: 6, background: "#E2E8F0", borderRadius: 3, overflow: "hidden" }}>
                        <div
                          style={{
                            width: `${router.health_score}%`,
                            height: "100%",
                            background: router.health_score > 80 ? "#10B981" : router.health_score > 50 ? "#F59E0B" : "#EF4444",
                            borderRadius: 3,
                          }}
                        />
                      </div>
                      <span style={{ fontSize: 11, fontWeight: 700, color: "#334155" }}>{router.health_score}%</span>
                    </div>
                  </td>
                  <td style={{ padding: "12px 14px" }}>
                    <span
                      style={{
                        fontSize: 10,
                        fontWeight: 700,
                        padding: "3px 8px",
                        borderRadius: 6,
                        background: router.management_type === "agent" ? "#E0F2FE" : "#F3E8FF",
                        color: router.management_type === "agent" ? "#0369A1" : "#6B21A8",
                        border: router.management_type === "agent" ? "1px solid #BAE6FD" : "1px solid #E9D5FF",
                        textTransform: "uppercase",
                      }}
                    >
                      {formatManagementLabel(router)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Selected Router Detail Modal */}
      {selectedRouter && (
        <ServerDetailModal server={selectedRouter} onClose={() => setSelectedRouter(null)} />
      )}
    </div>
  );
}
